/**
 * Unify FHIR Consent model — ROI and session-recording consents.
 *
 * ## ROI
 * A FHIR Consent is the canonical Release of Information authorization (category `roi`).
 *
 * ## Session recording
 * Category `session-recording` stores telehealth recording opt-in/out. Decision is
 * status alone (`active` / `rejected` / `inactive` / `draft`); no `provision`.
 *
 * ## AI tools
 * Category `ai-tools` stores a signed client consent for AI documentation.
 * `status: active` allows AI features. `provision.type` is the transcription
 * choice (`permit` uses meeting transcripts; `deny` does not). `inactive` is revoked.
 */

import type { Consent, ConsentProvisionActor, Coding, CodeableConcept, Extension, QuestionnaireResponse, QuestionnaireResponseItem, Reference, Patient, RelatedPerson } from '@medplum/fhirtypes';
import {
  AI_TOOLS_CONSENT_CATEGORY_CODE,
  AI_TOOLS_CONSENT_LINK_IDS,
  AI_TOOLS_CONSENT_QUESTIONNAIRE_ID,
  AI_TOOLS_CONSENT_QUESTIONNAIRE_URL,
  CLIENT_SIGNATURE_EXT_URL,
  CONSENT_CATEGORY_SYSTEM,
  ROI_CONSENT_CATEGORY_CODE,
  ROI_CONSENT_PURPOSE_SYSTEM,
  CONSENT_SCOPE_SYSTEM,
  CONSENT_ACTOR_ROLE_SYSTEM,
  CONSENT_ACTOR_IRCP_CODE,
  ROI_PATIENT_CONTACT_NAME_EXT,
  ROI_CONSENT_OTHER_DESCRIPTION_EXT,
  SESSION_RECORDING_CONSENT_CATEGORY_CODE,
} from '../constants';

// ─── Types ───────────────────────────────────────────────────────────

export type RoiConsentStatus = 'active' | 'inactive' | 'draft';

/** Purpose codes stored in `Consent.provision.purpose[]` for ROI. */
export type RoiPurposeCode =
  | 'medicalHistoryAndEvaluations'
  | 'mentalHealthEvaluations'
  | 'developmentalAndSocialHistory'
  | 'educationalRecords'
  | 'progressNotesAndTreatmentSummary'
  | 'other';

// ─── Accessors ───────────────────────────────────────────────────────

/** Returns true when the Consent is a Unify ROI record. */
export function isRoiConsent(consent: Consent): boolean {
  return (consent.category ?? []).some((cc) =>
    (cc.coding ?? []).some(
      (c) => c.system === CONSENT_CATEGORY_SYSTEM && c.code === ROI_CONSENT_CATEGORY_CODE,
    ),
  );
}

/** Returns the ROI status from Consent.status. */
export function getRoiConsentStatus(consent: Consent): RoiConsentStatus {
  const s = consent.status;
  if (s === 'inactive' || s === 'draft') return s;
  return 'active';
}

/**
 * Returns the ROI purpose codes from Consent.provision.purpose.
 * Note: ConsentProvision.purpose is Coding[] (flat), not CodeableConcept[].
 */
export function getRoiPurposeCodes(consent: Consent): RoiPurposeCode[] {
  return (consent.provision?.purpose ?? [])
    .filter((c) => c.system === ROI_CONSENT_PURPOSE_SYSTEM)
    .map((c) => c.code as RoiPurposeCode)
    .filter(Boolean);
}

/** Returns the "other" description extension value. */
export function getRoiOtherDescription(consent: Consent): string | undefined {
  return consent.extension?.find((e) => e.url === ROI_CONSENT_OTHER_DESCRIPTION_EXT)?.valueString;
}

/** Returns the patient-contact display name extension value (for Patient.contact entries). */
export function getRoiPatientContactName(consent: Consent): string | undefined {
  return consent.extension?.find((e) => e.url === ROI_PATIENT_CONTACT_NAME_EXT)?.valueString;
}

/** Returns the RelatedPerson reference from provision.actor, if present. */
export function getRoiRelatedPersonRef(consent: Consent): string | undefined {
  return (consent.provision?.actor ?? [])
    .map((a) => a.reference?.reference)
    .find((r) => r?.startsWith('RelatedPerson/'));
}

// ─── Builders ─────────────────────────────────────────────────────────

/** Satisfies FHIR Consent constraint ppc-1 (policy or policyRule required). */
export function roiConsentPolicyRule(): CodeableConcept {
  return {
    coding: [{
      system: CONSENT_CATEGORY_SYSTEM,
      code: ROI_CONSENT_CATEGORY_CODE,
      display: 'Release of Information',
    }],
    text: 'Release of Information authorization permitting disclosure of protected health information to the designated contact.',
  };
}

export interface BuildRoiConsentParams {
  patientId: string;
  /** FHIR RelatedPerson id — present when contact.source === 'related-person'. */
  relatedPersonId?: string;
  /** Display name (First Last) for Patient.contact entries without a RelatedPerson id. */
  patientContactName?: string;
  /** Required when status is active; omitted for draft until the client signs. */
  dateSigned?: string;
  expirationDate?: string;
  purposeCodes: RoiPurposeCode[];
  otherDescription?: string;
  status?: RoiConsentStatus;
}

export function buildRoiConsent(params: BuildRoiConsentParams): Consent {
  const actors: ConsentProvisionActor[] = [];
  if (params.relatedPersonId) {
    actors.push({
      role: {
        coding: [{ system: CONSENT_ACTOR_ROLE_SYSTEM, code: CONSENT_ACTOR_IRCP_CODE, display: 'Information Recipient' }],
      },
      reference: { reference: `RelatedPerson/${params.relatedPersonId}` } as Reference<RelatedPerson>,
    });
  }

  // ConsentProvision.purpose is Coding[] (flat), not CodeableConcept[]
  const purposes: Coding[] = params.purposeCodes.map((code) => ({
    system: ROI_CONSENT_PURPOSE_SYSTEM,
    code,
  }));

  const extensions: Extension[] = [];
  if (params.patientContactName) {
    extensions.push({ url: ROI_PATIENT_CONTACT_NAME_EXT, valueString: params.patientContactName });
  }
  if (params.otherDescription) {
    extensions.push({ url: ROI_CONSENT_OTHER_DESCRIPTION_EXT, valueString: params.otherDescription });
  }

  return {
    resourceType: 'Consent',
    status: params.status ?? 'active',
    scope: {
      coding: [{ system: CONSENT_SCOPE_SYSTEM, code: 'patient-privacy', display: 'Privacy Consent' }],
    },
    category: [
      {
        coding: [{ system: CONSENT_CATEGORY_SYSTEM, code: ROI_CONSENT_CATEGORY_CODE, display: 'Release of Information' }],
      },
    ],
    patient: { reference: `Patient/${params.patientId}` } as Reference<Patient>,
    dateTime: new Date().toISOString(),
    policyRule: roiConsentPolicyRule(),
    provision: {
      type: 'permit',
      period: {
        ...(params.dateSigned ? { start: params.dateSigned } : {}),
        end: params.expirationDate,
      },
      actor: actors.length > 0 ? actors : undefined,
      purpose: purposes.length > 0 ? purposes : undefined,
    },
    extension: extensions.length > 0 ? extensions : undefined,
  };
}

// ─── Session recording Consent ───────────────────────────────────────

export type SessionRecordingDecision = 'none' | 'pending' | 'revoked' | 'permitted' | 'denied';
export type SessionRecordingProvisionDecision = 'permit' | 'deny';

const ISO_UTC_Z_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;

/** Returns true when any category coding is session-recording. */
export function isSessionRecordingConsent(consent: Consent): boolean {
  return (consent.category ?? []).some((cc) =>
    (cc.coding ?? []).some(
      (c) => c.system === CONSENT_CATEGORY_SYSTEM && c.code === SESSION_RECORDING_CONSENT_CATEGORY_CODE,
    ),
  );
}

export function sessionRecordingConsentPolicyRule(): CodeableConcept {
  return {
    coding: [{
      system: CONSENT_CATEGORY_SYSTEM,
      code: SESSION_RECORDING_CONSENT_CATEGORY_CODE,
      display: 'Session Recording',
    }],
    text: 'Consent for recording telehealth sessions for documentation.',
  };
}

/** Strict semantic ISO-8601 UTC instant with `Z` (matches `Date.prototype.toISOString()` shape). */
export function isValidSessionRecordingDateTime(value: string): boolean {
  if (!ISO_UTC_Z_RE.test(value)) return false;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return false;
  const d = new Date(ms);
  const y = Number(value.slice(0, 4));
  const mo = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const hh = Number(value.slice(11, 13));
  const mm = Number(value.slice(14, 16));
  const ss = Number(value.slice(17, 19));
  return (
    d.getUTCFullYear() === y
    && d.getUTCMonth() + 1 === mo
    && d.getUTCDate() === day
    && d.getUTCHours() === hh
    && d.getUTCMinutes() === mm
    && d.getUTCSeconds() === ss
  );
}

function compareDateTimeDesc(a: Consent, b: Consent): number {
  const aValid = a.dateTime && isValidSessionRecordingDateTime(a.dateTime);
  const bValid = b.dateTime && isValidSessionRecordingDateTime(b.dateTime);
  if (aValid && bValid) {
    const cmp = (b.dateTime as string).localeCompare(a.dateTime as string);
    if (cmp !== 0) return cmp;
  } else if (aValid && !bValid) return -1;
  else if (!aValid && bValid) return 1;
  return (b.id ?? '').localeCompare(a.id ?? '');
}

export interface ResolveSessionRecordingResult {
  decision: SessionRecordingDecision;
  /** Authoritative Consent when decision is permitted / denied / revoked. */
  consent?: Consent;
}

/**
 * Resolve current session-recording decision from a complete Consent collection
 * for one patient (caller must page until exhausted).
 */
export function resolveCurrentSessionRecordingDecision(
  consents: Consent[],
  patientId: string,
): ResolveSessionRecordingResult {
  const patientRef = `Patient/${patientId}`;
  const matching = consents.filter((c) => {
    if (!isSessionRecordingConsent(c)) return false;
    if (c.patient?.reference !== patientRef) return false;
    const status = c.status;
    if (status !== 'active' && status !== 'rejected' && status !== 'inactive' && status !== 'draft') {
      return false;
    }
    if (c.dateTime != null && c.dateTime !== '' && !isValidSessionRecordingDateTime(c.dateTime)) {
      return false;
    }
    return true;
  });

  const authoritative = matching
    .filter((c) => c.status === 'active' || c.status === 'rejected' || c.status === 'inactive')
    .sort(compareDateTimeDesc);

  const top = authoritative[0];
  if (top?.status === 'active') return { decision: 'permitted', consent: top };
  if (top?.status === 'rejected') return { decision: 'denied', consent: top };
  if (top?.status === 'inactive') return { decision: 'revoked', consent: top };

  const drafts = matching.filter((c) => c.status === 'draft').sort(compareDateTimeDesc);
  if (drafts[0]) return { decision: 'pending', consent: drafts[0] };
  return { decision: 'none' };
}

export interface BuildSessionRecordingConsentParams {
  patientId: string;
  decision: SessionRecordingProvisionDecision;
  /** Strict UTC `Z` instant — when consent was given. */
  dateTime: string;
  currentDecision: SessionRecordingDecision;
  /** Required when currentDecision is permitted or denied. */
  priorConsent?: Consent;
  /** Optional link to the signed QuestionnaireResponse. */
  sourceQuestionnaireResponseId?: string;
}

export function buildSessionRecordingConsent(params: BuildSessionRecordingConsentParams): Consent {
  const { patientId, decision, dateTime, currentDecision, priorConsent, sourceQuestionnaireResponseId } = params;

  if (!isValidSessionRecordingDateTime(dateTime)) {
    throw new Error('buildSessionRecordingConsent: dateTime must be a semantic ISO-8601 UTC instant ending in Z');
  }

  if (currentDecision === 'permitted' && decision === 'permit') {
    throw new Error('buildSessionRecordingConsent: redundant permit while already permitted');
  }
  if (currentDecision === 'denied' && decision === 'deny') {
    throw new Error('buildSessionRecordingConsent: redundant deny while already denied');
  }

  if (currentDecision === 'permitted' || currentDecision === 'denied') {
    if (!priorConsent) {
      throw new Error('buildSessionRecordingConsent: priorConsent required when superseding permitted/denied');
    }
    if (!isSessionRecordingConsent(priorConsent)) {
      throw new Error('buildSessionRecordingConsent: priorConsent must be session-recording');
    }
    const expectedStatus = currentDecision === 'permitted' ? 'active' : 'rejected';
    if (priorConsent.status !== expectedStatus) {
      throw new Error(`buildSessionRecordingConsent: priorConsent.status must be ${expectedStatus}`);
    }
    if (!priorConsent.dateTime || !isValidSessionRecordingDateTime(priorConsent.dateTime)) {
      throw new Error('buildSessionRecordingConsent: priorConsent.dateTime must be a valid UTC Z instant');
    }
    if (!(dateTime > priorConsent.dateTime)) {
      throw new Error('buildSessionRecordingConsent: dateTime must be strictly after priorConsent.dateTime');
    }
  }

  const status = decision === 'permit' ? 'active' : 'rejected';
  const consent: Consent = {
    resourceType: 'Consent',
    status,
    scope: {
      coding: [{ system: CONSENT_SCOPE_SYSTEM, code: 'patient-privacy', display: 'Privacy Consent' }],
    },
    category: [
      {
        coding: [{
          system: CONSENT_CATEGORY_SYSTEM,
          code: SESSION_RECORDING_CONSENT_CATEGORY_CODE,
          display: 'Session Recording',
        }],
      },
    ],
    patient: { reference: `Patient/${patientId}` } as Reference<Patient>,
    dateTime,
    policyRule: sessionRecordingConsentPolicyRule(),
  };

  if (sourceQuestionnaireResponseId) {
    consent.sourceReference = {
      reference: `QuestionnaireResponse/${sourceQuestionnaireResponseId}`,
    };
  }

  return consent;
}

/** Returns a copy with status inactive (active or rejected session-recording only). */
export function inactivateSessionRecordingConsent(consent: Consent): Consent {
  if (!isSessionRecordingConsent(consent)) {
    throw new Error('inactivateSessionRecordingConsent: not a session-recording Consent');
  }
  if (consent.status !== 'active' && consent.status !== 'rejected') {
    throw new Error('inactivateSessionRecordingConsent: status must be active or rejected');
  }
  return { ...consent, status: 'inactive' };
}

/** Returns a copy with status inactive (active session-recording permit only). */
export function revokeSessionRecordingConsent(consent: Consent): Consent {
  if (!isSessionRecordingConsent(consent)) {
    throw new Error('revokeSessionRecordingConsent: not a session-recording Consent');
  }
  if (consent.status !== 'active') {
    throw new Error('revokeSessionRecordingConsent: status must be active');
  }
  return { ...consent, status: 'inactive' };
}

// ─── AI tools Consent ────────────────────────────────────────────────

export type AiTranscriptionDecision = 'permit' | 'deny';
/** `active` = signed form on file. `revoked` = client turned AI off. `none` = never signed. */
export type AiToolsConsentDecision = 'none' | 'active' | 'revoked';

export interface AiToolsConsentState {
  decision: AiToolsConsentDecision;
  transcription?: AiTranscriptionDecision;
  consent?: Consent;
  /** Set when the state was derived from a signed QuestionnaireResponse and no Consent exists yet. */
  sourceQuestionnaireResponseId?: string;
  dateTime?: string;
}

export function isAiToolsConsent(consent: Consent): boolean {
  return (consent.category ?? []).some((cc) =>
    (cc.coding ?? []).some(
      (c) => c.system === CONSENT_CATEGORY_SYSTEM && c.code === AI_TOOLS_CONSENT_CATEGORY_CODE,
    ),
  );
}

export function aiToolsConsentPolicyRule(): CodeableConcept {
  return {
    coding: [{
      system: CONSENT_CATEGORY_SYSTEM,
      code: AI_TOOLS_CONSENT_CATEGORY_CODE,
      display: 'AI Tools',
    }],
    text: 'Consent for artificial-intelligence tools used in clinical documentation.',
  };
}

export function getAiTranscriptionDecision(consent: Consent): AiTranscriptionDecision | undefined {
  const type = consent.provision?.type;
  if (type === 'permit' || type === 'deny') return type;
  return undefined;
}

export function isAiToolsConsentQuestionnaireRef(value: string | undefined): boolean {
  if (!value?.trim()) return false;
  const raw = value.trim();
  const urlOnly = raw.includes('|') ? raw.split('|')[0]! : raw;
  if (urlOnly === AI_TOOLS_CONSENT_QUESTIONNAIRE_URL) return true;
  if (urlOnly === AI_TOOLS_CONSENT_QUESTIONNAIRE_ID) return true;
  if (urlOnly === `Questionnaire/${AI_TOOLS_CONSENT_QUESTIONNAIRE_ID}`) return true;
  if (urlOnly.endsWith(`/${AI_TOOLS_CONSENT_QUESTIONNAIRE_ID}`)) return true;
  return false;
}

function collectLinkItems(
  items: QuestionnaireResponseItem[] | undefined,
  linkId: string,
  out: QuestionnaireResponseItem[] = [],
): QuestionnaireResponseItem[] {
  for (const item of items ?? []) {
    if (item.linkId === linkId) out.push(item);
    if (item.item?.length) collectLinkItems(item.item, linkId, out);
  }
  return out;
}

/** Reads the required transcription choice. Throws when the answer is missing or not permit/deny. */
export function parseAiTranscriptionDecisionFromQr(qr: QuestionnaireResponse): AiTranscriptionDecision {
  const matches = collectLinkItems(qr.item, AI_TOOLS_CONSENT_LINK_IDS.transcription);
  if (matches.length !== 1) {
    throw new Error(
      `AI tools QuestionnaireResponse must contain exactly one "${AI_TOOLS_CONSENT_LINK_IDS.transcription}" item`,
    );
  }
  const answers = matches[0]!.answer ?? [];
  if (answers.length !== 1) {
    throw new Error(`AI transcription decision must have exactly one answer (got ${answers.length})`);
  }
  const answer = answers[0]!;
  const code = answer.valueCoding?.code?.trim()
    ?? (typeof answer.valueString === 'string' ? answer.valueString.trim() : undefined);
  if (code !== 'permit' && code !== 'deny') {
    throw new Error('AI transcription decision must be permit or deny');
  }
  return code;
}

/** True only when the response has exactly one client-signature extension with non-empty signature data. */
export function qrHasClientSignature(qr: QuestionnaireResponse): boolean {
  const matches = (qr.extension ?? []).filter((e) => e.url === CLIENT_SIGNATURE_EXT_URL);
  if (matches.length !== 1) return false;
  const raw = matches[0]?.valueString;
  if (!raw?.trim()) return false;
  try {
    const payload = JSON.parse(raw) as { signatureData?: unknown };
    const signatureData = typeof payload.signatureData === 'string' ? payload.signatureData.trim() : '';
    return signatureData.length > 0;
  } catch {
    return false;
  }
}

function qrConsentInstant(qr: QuestionnaireResponse): string | undefined {
  for (const candidate of [qr.authored, qr.meta?.lastUpdated]) {
    if (candidate && isValidSessionRecordingDateTime(candidate)) return candidate;
  }
  return undefined;
}

export interface BuildAiToolsConsentParams {
  patientId: string;
  transcription: AiTranscriptionDecision;
  /** Strict UTC `Z` instant — when the form was signed. */
  dateTime: string;
  sourceQuestionnaireResponseId?: string;
}

export function buildAiToolsConsent(params: BuildAiToolsConsentParams): Consent {
  const { patientId, transcription, dateTime, sourceQuestionnaireResponseId } = params;
  if (!isValidSessionRecordingDateTime(dateTime)) {
    throw new Error('buildAiToolsConsent: dateTime must be a semantic ISO-8601 UTC instant ending in Z');
  }
  const consent: Consent = {
    resourceType: 'Consent',
    status: 'active',
    scope: {
      coding: [{ system: CONSENT_SCOPE_SYSTEM, code: 'patient-privacy', display: 'Privacy Consent' }],
    },
    category: [{
      coding: [{
        system: CONSENT_CATEGORY_SYSTEM,
        code: AI_TOOLS_CONSENT_CATEGORY_CODE,
        display: 'AI Tools',
      }],
    }],
    patient: { reference: `Patient/${patientId}` } as Reference<Patient>,
    dateTime,
    policyRule: aiToolsConsentPolicyRule(),
    provision: { type: transcription },
  };
  if (sourceQuestionnaireResponseId) {
    consent.sourceReference = { reference: `QuestionnaireResponse/${sourceQuestionnaireResponseId}` };
  }
  return consent;
}

/** Returns a copy with status inactive (active AI-tools consent only). */
export function revokeAiToolsConsent(consent: Consent): Consent {
  if (!isAiToolsConsent(consent)) {
    throw new Error('revokeAiToolsConsent: not an AI-tools Consent');
  }
  if (consent.status !== 'active') {
    throw new Error('revokeAiToolsConsent: status must be active');
  }
  return { ...consent, status: 'inactive' };
}

export function resolveCurrentAiToolsConsent(
  consents: Consent[],
  patientId: string,
): AiToolsConsentState {
  const patientRef = `Patient/${patientId}`;
  const matching = consents.filter((c) => {
    if (!isAiToolsConsent(c)) return false;
    if (c.patient?.reference !== patientRef) return false;
    const status = c.status;
    if (status !== 'active' && status !== 'inactive') return false;
    if (c.dateTime != null && c.dateTime !== '' && !isValidSessionRecordingDateTime(c.dateTime)) return false;
    return true;
  });
  const authoritative = matching
    .filter((c) => c.status === 'active' || c.status === 'inactive')
    .sort(compareDateTimeDesc);
  const top = authoritative[0];
  if (top?.status === 'active') {
    return { decision: 'active', transcription: getAiTranscriptionDecision(top), consent: top, dateTime: top.dateTime };
  }
  if (top?.status === 'inactive') {
    return { decision: 'revoked', transcription: getAiTranscriptionDecision(top), consent: top, dateTime: top.dateTime };
  }
  return { decision: 'none' };
}

/**
 * Consent wins when one exists. A revoked consent is not replaced by an older signed form.
 * When no consent exists, the latest completed response with signature data supplies the decision.
 */
export function resolveAiToolsConsentState(
  consents: Consent[],
  patientId: string,
  questionnaireResponses?: QuestionnaireResponse[],
): AiToolsConsentState {
  const fromConsent = resolveCurrentAiToolsConsent(consents, patientId);
  if (fromConsent.decision !== 'none') return fromConsent;

  let best: { transcription: AiTranscriptionDecision; dateTime: string; id?: string } | undefined;
  for (const qr of questionnaireResponses ?? []) {
    if (qr.status !== 'completed') continue;
    if (!isAiToolsConsentQuestionnaireRef(qr.questionnaire)) continue;
    if (!qrHasClientSignature(qr)) continue;
    const dateTime = qrConsentInstant(qr);
    if (!dateTime) continue;
    let transcription: AiTranscriptionDecision;
    try {
      transcription = parseAiTranscriptionDecisionFromQr(qr);
    } catch {
      continue;
    }
    if (!best || dateTime > best.dateTime) {
      best = { transcription, dateTime, id: qr.id };
    }
  }
  if (!best) return { decision: 'none' };
  return {
    decision: 'active',
    transcription: best.transcription,
    sourceQuestionnaireResponseId: best.id,
    dateTime: best.dateTime,
  };
}
